import { motion } from "framer-motion";
import { useCMS } from "@/hooks/useCMS";
import SEO from "@/components/SEO";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.12, duration: 0.7, ease: "easeOut" } }),
};

/* Hardcoded fallback data — CMS overrides when available */
const defaultUpcoming = [];
const defaultPast = [
  { title: "Halcyon", date: "2026", flyer: "/api/files/phoon-site/uploads/0367d2a1-7344-4cfb-afd0-a229730d264b.jpeg" },
  { title: "Destination Festival", date: "2024", flyer: "/api/files/phoon-site/uploads/33347c1e-6045-42a5-9c93-5aa495917565.jpeg" },
  { title: "Private Event", date: "2024", flyer: "/api/files/phoon-site/uploads/d042570d-9b35-44ec-aa53-ae964f87ad46.png" },
  { title: "", date: "", flyer: "/api/files/phoon-site/uploads/31c4e4d1-fa98-41e1-a433-ac26e0b1c97f.jpeg" },
  { title: "", date: "", flyer: "/api/files/phoon-site/uploads/a6830e7b-f644-4741-b30b-7b19ff2b938a.jpeg" },
  { title: "", date: "", flyer: "/api/files/phoon-site/uploads/3f70b60e-7d0c-4f41-b2a7-754f84bb3d36.jpeg" },
  { title: "", date: "", flyer: "/api/files/phoon-site/uploads/76b02691-65a8-4f7a-a2fc-a2f207396b40.jpeg" },
  { title: "", date: "", flyer: "/api/files/phoon-site/uploads/30dcb22b-7269-4c8d-90f6-39ce69ef20b2.jpeg" },
  { title: "", date: "", flyer: "/api/files/phoon-site/uploads/c96b5558-335a-4607-9037-8673e785cbc1.jpeg" },
  { title: "", date: "", flyer: "/api/files/phoon-site/uploads/99566795-e891-4907-99be-dd199899fed0.jpeg" },
  { title: "", date: "", flyer: "/api/files/phoon-site/uploads/bec2296e-8831-48a6-8970-eee8f7e338e3.jpeg" },
  { title: "", date: "", flyer: "/api/files/phoon-site/uploads/15d13a24-9437-4290-87b2-955b040ecb14.jpeg" },
  { title: "", date: "", flyer: "/api/files/phoon-site/uploads/18bdfc4f-165c-4df8-9ad8-f13a93b43220.jpeg" },
  { title: "", date: "", flyer: "/api/files/phoon-site/uploads/10faa86c-56fc-49a2-9326-294306729cfd.jpeg" },
];

export default function Shows() {
  const { content } = useCMS();
  const shows = content.shows || {};
  const upcomingShows = (shows.upcoming && shows.upcoming.length > 0) ? shows.upcoming : defaultUpcoming;
  const pastShows = (shows.past && shows.past.length > 0) ? shows.past : defaultPast;
  return (
    <main data-testid="shows-page" className="pt-20">
      <SEO title="Shows" description="Upcoming shows and past performances by Phöön. House and tech house events." path="/shows" />
      {/* Page Header */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.h1
            data-testid="shows-page-title"
            className="font-['Black_Han_Sans'] text-5xl md:text-7xl text-[#F4EFE6] uppercase"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Shows
          </motion.h1>
        </div>
      </section>

      {/* Upcoming Shows */}
      <section data-testid="upcoming-shows-section" className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p
            className="font-mono-label text-base text-[#FF5500] mb-10"
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}
          >
            Upcoming
          </motion.p>

          {upcomingShows.length > 0 ? (
            <div className="space-y-0">
              {upcomingShows.map((show, i) => (
                <motion.a
                  key={`upcoming-${show.venue}-${show.date}-${i}`}
                  href={show.link}
                  target={show.link !== "#" ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  data-testid={`upcoming-show-${i}`}
                  className="flex items-center justify-between py-6 border-b border-[#FF5500]/15 group transition-colors hover:bg-[#0c1128]/30"
                  initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} variants={fadeInUp}
                >
                  <div className="flex items-baseline gap-6 md:gap-10">
                    <span className="font-mono-label text-xs text-[#FF5500] w-20 md:w-28 shrink-0">{show.date}</span>
                    <div>
                      <span className="font-['Black_Han_Sans'] text-xl md:text-2xl text-[#F4EFE6] group-hover:text-[#FF5500] transition-colors duration-300 uppercase">
                        {show.venue}
                      </span>
                      {show.details && (
                        <span className="text-[#F4EFE6]/60 text-sm ml-3">{show.details}</span>
                      )}
                    </div>
                  </div>
                  <span className="font-mono-label text-[10px] text-[#F4EFE6]/60 shrink-0">{show.city}</span>
                </motion.a>
              ))}
            </div>
          ) : (
            <p className="text-[#F4EFE6]/60">No upcoming shows announced. Check back soon.</p>
          )}
        </div>
      </section>

      {/* Past Performances — Flyer Grid */}
      <section data-testid="past-shows-section" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p
            className="font-mono-label text-base text-[#FF5500] mb-10"
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}
          >
            Past Performances
          </motion.p>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
            {pastShows.map((show, i) => (
              <motion.div
                key={`past-${show.title}-${show.date}-${i}`}
                data-testid={`past-show-${i}`}
                className="group"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={fadeInUp}
              >
                <div className="relative overflow-hidden border border-[#FF5500]/10 group-hover:border-[#FF5500]/40 transition-colors duration-300">
                  <img
                    src={show.flyer}
                    alt={`${show.title} flyer`}
                    loading="lazy"
                    className="w-full aspect-square object-cover object-center transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
